import React, { createContext, useState, useEffect, useCallback, ReactNode } from "react";
import api, { setAccessToken, getCsrf } from "../api";
import { User } from "../types/User";

interface AuthContextProps {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (userData: User, accessToken: string) => void;
  logout: () => void;
}

export const AuthContext = createContext<AuthContextProps>({} as AuthContextProps);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        await getCsrf();

        const { data: { accessToken } } = await api.post("/auth/refresh-token");
        setAccessToken(accessToken);

        const { data: userData } = await api.get<User>("/auth/user");
        setUser(userData);
        setIsAuthenticated(true);

        if (userData.status === "pending_delete") {
          try {
            await api.post(`/users/${userData.slug}/cancel-deletion`);
            const { data: refreshed } = await api.get<User>("/auth/user");
            setUser(refreshed);
          } catch {}
        }
      } catch {
        setIsAuthenticated(false);
        setAccessToken(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = useCallback((userData: User, accessToken: string) => {
    setAccessToken(accessToken);
    setUser(userData);
    setIsAuthenticated(true);
    (async () => {
      if (userData?.status === "pending_delete") {
        try {
          await api.post(`/users/${userData.slug}/cancel-deletion`);
          const { data: refreshed } = await api.get<User>("/auth/user");
          setUser(refreshed);
        } catch {}
      }
    })();
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch {}
    setAccessToken(null);
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
